import type { Router } from 'vue-router';

/**
 * Was the current view reached by the customer going back/forward through
 * history, rather than by navigating somewhere?
 *
 * The payment page auto-checks-in any customer who has a payment method, so a
 * history entry of it is a loaded gun: pressing back after a receipt lands on
 * that entry, and the page would open the fridge again — a new order, a new
 * €5 authorisation, and an unattended unlock. A traversal is never an intent
 * to check in, so the page needs to know how it was reached.
 *
 * Two signals, because the history stack mixes documents and SPA routes: the
 * paygate round trip creates a new document (so back from the receipt is a
 * document-level 'back_forward' load, relevant only to that document's FIRST
 * route), while everything else is an in-app popstate ('pop' history event).
 */
export function isHistoryTraversal(signals: {
  isInitialRoute: boolean;
  documentNavigationType: string | undefined;
  historyEventType: string | undefined;
}): boolean {
  if (signals.historyEventType === 'pop') {
    return true;
  }
  return (
    signals.isInitialRoute && signals.documentNavigationType === 'back_forward'
  );
}

let lastNavigationWasTraversal = false;

/**
 * Wires the tracking into the router once, at app start. Afterwards
 * {@link lastNavigationWasHistoryTraversal} answers for the route currently
 * shown.
 */
export function installHistoryTraversalTracking(router: Router): void {
  const entry = (
    typeof performance !== 'undefined' && performance.getEntriesByType
      ? performance.getEntriesByType('navigation')
      : []
  )[0] as PerformanceNavigationTiming | undefined;
  const documentNavigationType = entry?.type;

  let pendingHistoryEventType: string | undefined;
  let isInitialRoute = true;

  // Fires for popstate-driven navigations only, before the router resolves them.
  router.options.history.listen((_to, _from, info) => {
    pendingHistoryEventType = info.type;
  });

  router.afterEach(() => {
    lastNavigationWasTraversal = isHistoryTraversal({
      isInitialRoute,
      documentNavigationType,
      historyEventType: pendingHistoryEventType,
    });
    pendingHistoryEventType = undefined;
    isInitialRoute = false;
  });
}

export function lastNavigationWasHistoryTraversal(): boolean {
  return lastNavigationWasTraversal;
}
